import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import clientService from 'src/clientService';
import { DeploymentCreate, DeviceAdminDetailWithDeployment } from 'src/api/Api';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';

import { DeploymentAdminDetail } from 'src/api/Api';

interface DeploymentDialogProps {
  open: boolean;
  onClose: () => void;
  locationId: string;
  onDeploymentCreated: (newDeployment: DeploymentAdminDetail) => void;
}

const DeploymentDialog: FC<DeploymentDialogProps> = ({ open, onClose, locationId, onDeploymentCreated }) => {
  const { showSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<DeploymentCreate>({} as DeploymentCreate);
  const [loading, setLoading] = useState<boolean>(false);
  const [devices, setDevices] = useState<DeviceAdminDetailWithDeployment[]>([]);
  const [errors, setErrors] = useState<{
    deployment_name?: string;
    device_id?: string;
    start_date?: string;
    end_date?: string;
  }>({});

  useEffect(() => {
    if (open) {
      clientService.api.getAllDevicesAdminApiV1AdminDevicesGet().then(response => {
        setDevices(response.data);
      });
      setFormData({} as DeploymentCreate);
      setErrors({}); // Clear errors when dialog opens
    }
  }, [open]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined })); // Clear error when user types
  };



  const validate = () => {
    const newErrors: typeof errors = {};
    if (!formData.deployment_name) {
      newErrors.deployment_name = 'Deployment Name is required';
    }
    if (!formData.device_id) {
      newErrors.device_id = 'Device is required';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Start Date is required';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'End Date is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      showSnackbar({
        message: 'Please fill in all required fields.',
        type: SnackbarType.ERROR,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await clientService.api.createApiDeploymentApiV1AdminDeploymentsPost({ ...formData, location_id: locationId });
      const createdDeployment = response.data as DeploymentAdminDetail;
      const selectedDevice = devices.find(device => device.device_id === formData.device_id);
      if (selectedDevice) {
        onDeploymentCreated({ ...createdDeployment, device: selectedDevice });
      } else {
        onDeploymentCreated(createdDeployment); // Fallback if device not found (shouldn't happen if validation is correct)
      }
      onClose();
      showSnackbar({
        message: 'Deployment created successfully!',
        type: SnackbarType.SUCCESS,
      });
    } catch (error) {
      console.error('Failed to create deployment', error);
      showSnackbar({
        message: 'Failed to create deployment.',
        type: SnackbarType.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add Deployment</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          name="deployment_name"
          label="Deployment Name"
          type="text"
          fullWidth
          variant="standard"
          value={formData.deployment_name || ''}
          onChange={handleChange}
          error={!!errors.deployment_name}
          helperText={errors.deployment_name}
          required
        />
        <TextField
          margin="dense"
          name="device_id"
          label="Device"
          select
          fullWidth
          SelectProps={{
            native: true,
            value: formData.device_id || '',
          }}
          variant="standard"
          onChange={handleChange}
          error={!!errors.device_id}
          helperText={errors.device_id}
          required
        >
          <option value=""></option>
          {devices.filter((d: DeviceAdminDetailWithDeployment) => !d.deployment_id).map(device => (
            <option key={device.device_id} value={device.device_id}>
              {device.human_readable_id} - {device.name}
            </option>
          ))}
        </TextField>
        <TextField
          margin="dense"
          name="start_date"
          label="Start Date"
          type="date"
          fullWidth
          variant="standard"
          InputLabelProps={{
            shrink: true,
          }}
          value={formData.start_date || ''}
          onChange={handleChange}
          error={!!errors.start_date}
          helperText={errors.start_date}
          required
          inputProps={{ "data-testid": "start-date-input" }}
        />
        <TextField
          margin="dense"
          name="end_date"
          label="End Date"
          type="date"
          fullWidth
          variant="standard"
          InputLabelProps={{
            shrink: true,
          }}
          value={formData.end_date || ''}
          onChange={handleChange}
          error={!!errors.end_date}
          helperText={errors.end_date}
          required
          inputProps={{ "data-testid": "end-date-input" }}
        />

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeploymentDialog;